import { useEffect, useState, useRef } from 'react'
import {
  useTransition,
  animated,
  config as springConfig,
} from '@react-spring/web'

import stylesSheet from './infoLayer.module.scss'
import { MarkerData } from 'Types/markers'
import styled, { useTheme } from 'styled-components'
import { device } from '../breakpoints'
import { Link } from '../styles'
import { Box } from '@mui/material'

import { ArticleTeaser } from '../articleLink'
import locales from 'locales/locales'

const getDuration = (start: Date, end: Date) => {
  const now = new Date()
  const latest = now > start ? now : start
  const diff = end.getTime() - latest.getTime()
  return Math.ceil(diff / 86400000) + 1
}

const prettifyDuration = (days: number) => {
  if (days <= 1) {
    return '1 jour'
  } else if (days > 365) {
    const remainder = days % 365
    if (remainder > 300) {
      // à 2 mois près: arrondi à l’année supérieure
      return `${Math.ceil(days / 365)} ans`
    } else {
      // dès 4 mois: on ajoute “et demi”
      const suffix = remainder > 120 ? ' et demi' : ''
      return `${Math.floor(days / 365)} ans${suffix}`
    }
  } else if (days > 50) {
    const remainder = days % 30.5
    if (remainder > 25) {
      // à 5 jours près: arrondi au mois supérieur
      return `${Math.ceil(days / 30.5)} mois`
    } else {
      // dès 15 jours: on ajoute “et demi”
      const suffix = remainder > 15 ? ' et demi' : ''
      return `${Math.floor(days / 30.5)} mois${suffix}`
    }
  } else {
    return `${days} jours`
  }
}

export const InfoLayer = ({
  activeMarker,
  handleClose,
  publisher,
  colorMode,
}: {
  activeMarker: MarkerData | null
  handleClose: () => void
  publisher: 'tdg' | '24heures'
  colorMode: 'light' | 'dark'
}) => {
  const [showInfoLayer, setShowInfoLayer] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const infoLayer = useRef(null)
  const wrapper = useRef(null)
  const theme = useTheme()

  const transitions = useTransition(showInfoLayer, {
    config: { ...springConfig.default, velocity: 0, mass: 0.1, tension: 450 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    onRest: () => {
      !showInfoLayer && handleClose()
    },
  })

  useEffect(() => {
    if (activeMarker) {
      setTimeout(() => {
        if (wrapper && wrapper?.current) {
          wrapper.current.scrollIntoView({ behaviour: 'smooth' })
        }
      }, 0)
      setShowInfoLayer(true)
      setImageLoaded(false)
    } else {
      setShowInfoLayer(false)
    }
  }, [activeMarker])

  return transitions(
    (styles, item) =>
      item && (
        <Wrapper ref={wrapper}>
          <Container
            ref={infoLayer}
            style={{
              transform: styles.opacity.to(
                (v) => 'translate3d(0,' + (1 - v) * 100 + '%,0)'
              ),
            }}
          >
            {activeMarker && (
              <MenuContainer>
                <MenuInfos>La carte des chantiers</MenuInfos>
                <CloseButton
                  onClick={() => {
                    setShowInfoLayer(false)
                  }}
                >
                  <Close />
                </CloseButton>
              </MenuContainer>
            )}

            <div className='ui vertical fitted segment info-footer'>
              <TextContainer>
                {activeMarker && (
                  <div>
                    <Title>{activeMarker.title}</Title>
                    <Infos>
                      {activeMarker.image && (
                        <ImageContainer>
                          <img
                            src={activeMarker.image}
                            alt={activeMarker.title}
                            style={imageLoaded ? {} : { opacity: 0 }}
                            onLoad={() => setImageLoaded(true)}
                          />
                        </ImageContainer>
                      )}
                      <Description>{activeMarker.description}</Description>
                      <InfoBox>
                        <Box mt={2} p={2}>
                          {activeMarker.start_date_str && (
                            <Info>
                              <InfoTitle style={{ marginTop: 0 }}>
                                Dates
                              </InfoTitle>
                              <div>
                                {activeMarker.start_date_str ===
                                activeMarker.end_date_str ? (
                                  <>Le {activeMarker.start_date_str}</>
                                ) : (
                                  <>
                                    Du {activeMarker.start_date_str} au{' '}
                                    {activeMarker.end_date_str}
                                  </>
                                )}
                              </div>
                            </Info>
                          )}
                          {activeMarker.duration_days && (
                            <Info>
                              <InfoTitle style={{ marginTop: 0 }}>
                                Temps restant prévu
                              </InfoTitle>
                              <div>
                                {prettifyDuration(
                                  getDuration(
                                    new Date(activeMarker.start_date),
                                    new Date(activeMarker.end_date)
                                  )
                                )}
                              </div>
                            </Info>
                          )}
                          {activeMarker.duration_days && (
                            <Info>
                              <InfoTitle style={{ marginTop: 0 }}>
                                Durée totale
                              </InfoTitle>
                              <div>
                                {prettifyDuration(
                                  Math.abs(activeMarker.duration_days)
                                )}
                              </div>
                            </Info>
                          )}
                          {activeMarker.project_owner && (
                            <Info>
                              <InfoTitle style={{ marginTop: 0 }}>
                                Maître d’ouvrage
                              </InfoTitle>
                              <div>{activeMarker.project_owner}</div>
                            </Info>
                          )}
                          {activeMarker.url && (
                            <Info>
                              <InfoTitle>Site internet</InfoTitle>
                              <div>
                                <A
                                  href={activeMarker.url}
                                  target='_blank'
                                  rel='noreferrer'
                                >
                                  {activeMarker.url.replace('https://', '')}
                                </A>
                              </div>
                            </Info>
                          )}
                          {activeMarker.address && (
                            <div>
                              <InfoTitle>Lieu</InfoTitle>
                              <div>{activeMarker.address}</div>
                            </div>
                          )}
                        </Box>
                      </InfoBox>
                    </Infos>

                    {activeMarker[`stories_${publisher}`]?.length > 0 && (
                      <TeaserContainer>
                        <ArticleTeaser
                          title='Lire également'
                          color={
                            publisher === 'tdg'
                              ? colorMode === 'dark'
                                ? '#5A64A8'
                                : '#2c3890'
                              : colorMode === 'dark'
                              ? '#A34746'
                              : '#d9010e'
                          }
                          pub={publisher}
                          ids={activeMarker[`stories_${publisher}`]}
                        />
                      </TeaserContainer>
                    )}
                  </div>
                )}
              </TextContainer>
            </div>

            <TextContainerBorder>
              <TextContainer>
                <div className={stylesSheet['filters-group']}>
                  <div className={stylesSheet['filters-group-title']}>
                    {activeMarker?.tag_list &&
                      activeMarker?.tag_list.map((text, index) => (
                        <InlineFilter key={`filter_${index}`}>
                          {text}
                        </InlineFilter>
                      ))}
                  </div>
                </div>
              </TextContainer>
            </TextContainerBorder>

            <InfoFooter>
              <InfoFooterContent>
                <Link href='mailto:interactif@tamedia.ch'>
                  {locales.readerUpdate[theme.lang]}
                </Link>
              </InfoFooterContent>
            </InfoFooter>
          </Container>
          <Mask
            style={{
              opacity: styles.opacity,
            }}
          />
        </Wrapper>
      )
  )
}

const Mask = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
`

const InlineFilter = styled.div`
  display: inline-block;
  cursor: pointer;
  padding: 0.45em 1.5rem;
  background: ${(props) => props.theme.greyColor};
  color: ${(props) => props.theme.textColor};
  border-radius: 50px;
  margin-right: 1rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;

  &:last-child {
    margin-right: 0;
  }
`

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 20px 20px 0px 0px;
  @media ${device.tablet} {
    width: 50%;
  }
`
const Container = styled(animated.div)`
  z-index: 999;
  position: sticky;
  transform: translate3d(0, 0, 0);
  left: 0px;
  width: 100%;
  overflow-y: scroll;
  background-color: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.085);

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    top: 10vh;
    height: 90vh;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    top: 0;
    height: 100vh;
  }

  overscroll-behavior-y: none;
  scroll-behavior: smooth;

  .text {
    color: ${(props) => props.theme.textColor};
  }

  .spacing-top {
    margin-top: 0.5em;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const Infos = styled.div`
  margin-top: 1rem;
  font-size: 1.5rem;
  line-height: 1.5;
  color: ${(props) => props.theme.textColor};

  img {
    max-width: 100%;
    margin: auto;
  }
`

const InfoFooter = styled.div`
  height: 10vh;
  display: flex;
  align-items: center;
`

const InfoFooterContent = styled.div`
  width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
`

const Info = styled.div`
  margin-top: 2rem;
  margin-bottom: 2rem;
`
const InfoTitle = styled.div`
  font-weight: 800;
`

const CloseButton = styled.div`
  background-color: var(--text-color);
  margin-left: auto;
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 10vh;
  position: absolute;
  right: 0;
  top: 0;

  &:after {
    content: '';
    margin-top: 75%;
  }
`

const Close = styled.div`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  opacity: 0.75;

  &:before,
  &:after {
    left: 15px;
    position: absolute;
    content: ' ';
    height: 33px;
    width: 2px;
    background-color: ${(props) =>
      props.theme.colorMode === 'dark' ? '#000000' : '#ffffff'};
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
`

const MenuContainer = styled(animated.div)`
  height: 60px;
  font-weight: 100;
  user-select: none;
  background-color: ${(props) => props.theme.plainColor};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  position: sticky;
  transform: translate3d(0, 0, 0);
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
`

const MenuInfos = styled.div`
  color: ${(props) => props.theme.mainColor};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  line-height: 1.5;
  padding-right: 2rem;
  width: calc(100% - 20vh -1em);
  float: left;
  height: 100%;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.5rem;
  max-width: 600px;
  padding: 0 2rem;
  margin: auto;
  width: 100%;
`

const Title = styled.div`
  font-family: var(--font-source);
  font-weight: 600;
  line-height: 1;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 3em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 4em;
  }
`

const Lead = styled.div`
  font-family: var(--font-plex);
  line-height: 1.35;
  margin-top: 1.25rem;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.85em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.85em;
  }
`

const Meta = styled.div`
  font-family: var(--font-plex);
  line-height: 1.35;
  margin-top: 1em;
  margin-bottom: 2em;
  opacity: 0.55;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.3em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.3em;
  }
`

const Description = styled.div`
  position: relative;
  font-family: var(--font-noto);
  margin: 2rem 0;
  margin-bottom: 0;
  font-size: 1.15em;
  line-height: 1.65;
`

const TextContainerBorder = styled.div``

const TextContainer = styled.div`
  max-width: 600px;
  margin: auto;
  padding: 0 2rem;
`
const InfoBox = styled.div`
  background-color: ${(props) => props.theme.greyColor};
`
const A = styled.a`
  word-break: break-all;
`

const ContinueReading = styled.div`
  color: white;
  display: inline-block;
  display: flex;
  align-items: center;

  &::before {
    font-weight: 700;
    display: inline-block;
    font-size: 0.75em;
    line-height: 1.25em;
    content: 'Abo';
    padding: 0.1em 0.5em 0.12em 0.5em;
    background-color: white;
    color: var(--text-color);
    margin-right: 0.65em;
  }
`

const ImageContainer = styled.div`
  @keyframes placeholderShimmer {
    0% {
      background-position: -1200px 0;
    }
    100% {
      background-position: 1200px 0;
    }
  }
  height: 100%;
  width: 100%;
  aspect-ratio: 2/1;
  background-color: #ffffff;
  background-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.15) 15%,
    rgba(0, 0, 0, 0.08) 30%
  );
  background-size: 1200px 100%;
  animation: placeholderShimmer 2s linear;
  animation-iteration-count: infinite;
  img {
    margin-bottom: -10px;
  }
`

const TeaserContainer = styled.div`
  max-width: 400px;
  margin: 1em auto 0 auto;
`
